import { useCallback, useState } from "react";
import { StyleSheet } from "react-native";
import {
  Button,
  ButtonGroup,
  DrawerActions,
  DrawerContent,
  DrawerHeader,
  SelectField,
} from "@smartrent/ui";

import { useDrawerNav } from "@/common/AppDrawer";
import { useGlobalContext } from "@/layout/Context";
import {
  useLinkAlloyAccessSiteMutation,
  useToggleAlloyAccessIntegrationMutation,
  useAlloyAccessOrgsSelectProps,
  useAlloyAccessSitesSelectProps,
  useTppViewQuery,
  AlloyAccessOrgRecord,
  AlloyAccessSiteRecord,
} from "@/api";
import TextInput from "@/common/TextInput";
import { useGroup } from "@/pages/group/GroupContext";

const ALLOY_ACCESS_TIMEZONES = [
  { label: "Hawaii (Pacific/Honolulu)", value: "Pacific/Honolulu" },
  { label: "Alaska (America/Anchorage)", value: "America/Anchorage" },
  { label: "Pacific (America/Los_Angeles)", value: "America/Los_Angeles" },
  { label: "Mountain (America/Denver)", value: "America/Denver" },
  { label: "Mountain No-DST (America/Phoenix)", value: "America/Phoenix" },
  { label: "Central (America/Chicago)", value: "America/Chicago" },
  { label: "Eastern (America/New_York)", value: "America/New_York" },
];

const SYSTEM_TYPE_TO_PROVIDER: Record<"alloy_access" | "salto", string> = {
  alloy_access: "Alloy Access",
  salto: "Alloy Access - Salto",
};

const SYSTEM_TYPE_LABEL: Record<"alloy_access" | "salto", string> = {
  alloy_access: "Alloy Access",
  salto: "Salto",
};

export interface EditAlloyAccessIntegrationProps {
  systemType: "alloy_access" | "salto";
}

export function EditAlloyAccessIntegration({
  systemType,
}: EditAlloyAccessIntegrationProps) {
  const { pop } = useDrawerNav();
  const { group } = useGroup();
  const { setToast } = useGlobalContext();

  const { data: tppViewData } = useTppViewQuery({
    filters: { groupId: group.id, tppc_scope: "group" },
  });
  const aaRecord = tppViewData?.records?.find(
    (r) => r.provider === SYSTEM_TYPE_TO_PROVIDER[systemType]
  );
  const existingEnabled: boolean | null = aaRecord?.enabled ?? null;

  const [toggleAlloyAccessIntegration, toggleIntegrationMutation] =
    useToggleAlloyAccessIntegrationMutation();

  const [aaMode, setAaMode] = useState<"select" | "create">("select");
  const [selectedOrg, setSelectedOrg] = useState<
    AlloyAccessOrgRecord | undefined
  >(undefined);
  const [selectedSite, setSelectedSite] = useState<
    AlloyAccessSiteRecord | undefined
  >(undefined);
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteTimezone, setNewSiteTimezone] = useState<string | null>(null);

  const [linkAlloyAccessSite, linkSiteMutation] =
    useLinkAlloyAccessSiteMutation();
  const orgSelectProps = useAlloyAccessOrgsSelectProps(group.id);
  const siteSelectProps = useAlloyAccessSitesSelectProps(
    group.id,
    selectedOrg?.id ?? null, // null means "no org selected, disable sites query"
    systemType
  );

  const handleLinkExistingSite = useCallback(async () => {
    if (!selectedSite) {
      return;
    }
    try {
      await linkAlloyAccessSite({
        groupId: group.id,
        site_id: selectedSite.id,
        system_type: selectedSite.system_type,
      });
      setToast({
        type: "success",
        title: "Success",
        message: "Site linked successfully.",
      });
      pop();
    } catch (error) {
      const solicitErrors = (error as any)?.response?.data?.errors;
      setToast({
        type: "error",
        title: "Error",
        message:
          solicitErrors || "Failed to link site. Please contact engineering.",
      });
    }
  }, [selectedSite, linkAlloyAccessSite, group.id, setToast, pop]);

  const handleCreateAndLinkSite = useCallback(async () => {
    if (!newSiteName || !selectedOrg || !newSiteTimezone) {
      return;
    }
    try {
      await linkAlloyAccessSite({
        groupId: group.id,
        name: newSiteName,
        organization_id: selectedOrg.id,
        timezone: newSiteTimezone,
        system_type: systemType,
      });
      setToast({
        type: "success",
        title: "Success",
        message: "Site created and linked successfully.",
      });
      pop();
    } catch (error) {
      const solicitErrors = (error as any)?.response?.data?.errors;
      setToast({
        type: "error",
        title: "Error",
        message:
          solicitErrors || "Failed to create site. Please contact engineering.",
      });
    }
  }, [
    newSiteName,
    selectedOrg,
    newSiteTimezone,
    systemType,
    linkAlloyAccessSite,
    group.id,
    setToast,
    pop,
  ]);

  return (
    <>
      <DrawerHeader
        title={`Edit ${SYSTEM_TYPE_LABEL[systemType]} Integration`}
      />
      <DrawerContent style={styles.drawerContent}>
        {existingEnabled !== null ? (
          <Button
            color={existingEnabled ? "destructive" : "primary"}
            disabled={toggleIntegrationMutation.isLoading}
            style={styles.toggleButton}
            onPress={async () => {
              try {
                await toggleAlloyAccessIntegration({
                  groupId: group.id,
                  enabled: !existingEnabled,
                  system_type: systemType,
                });
              } catch (error) {
                const solicitErrors = (error as any)?.response?.data?.errors;
                setToast({
                  type: "error",
                  title: "Error",
                  message:
                    solicitErrors ||
                    "Failed to update integration. Please contact engineering.",
                });
              }
            }}
          >
            {toggleIntegrationMutation.isLoading
              ? "Updating..."
              : existingEnabled
                ? "Disable Integration"
                : "Enable Integration"}
          </Button>
        ) : null}

        <SelectField<AlloyAccessOrgRecord>
          label="Organization"
          name="aa_organization"
          value={selectedOrg}
          onChange={(org) => {
            setSelectedOrg(org);
            setSelectedSite(undefined);
            setAaMode("select");
            setNewSiteName("");
            setNewSiteTimezone(null);
          }}
          getOptionValue={(org) => String(org?.id ?? "")}
          getOptionLabel={(org) => (org ? `${org.name} (id: ${org.id})` : "")}
          {...orgSelectProps}
        />

        {selectedOrg ? (
          <>
            <ButtonGroup<"select" | "create">
              value={aaMode}
              onChangeValue={setAaMode}
              options={[
                { label: "Select Existing Site", value: "select" },
                { label: "Create New Site", value: "create" },
              ]}
              style={styles.modeToggle}
            />

            {aaMode === "select" && (
              <>
                <SelectField<AlloyAccessSiteRecord>
                  label="Site"
                  name="aa_site"
                  value={selectedSite}
                  onChange={setSelectedSite}
                  getOptionValue={(site) => String(site?.id ?? "")}
                  getOptionLabel={(site) =>
                    site ? `${site.name} (id: ${site.id})` : ""
                  }
                  {...siteSelectProps}
                />
                <Button
                  disabled={!selectedSite || linkSiteMutation.isLoading}
                  onPress={handleLinkExistingSite}
                >
                  {linkSiteMutation.isLoading ? "Linking..." : "Link Site"}
                </Button>
              </>
            )}

            {aaMode === "create" && (
              <>
                <TextInput
                  label="Site name"
                  value={newSiteName}
                  onChangeText={setNewSiteName}
                />
                <SelectField
                  label="Timezone"
                  name="aa_timezone"
                  value={
                    newSiteTimezone
                      ? ALLOY_ACCESS_TIMEZONES.find(
                          (t) => t.value === newSiteTimezone
                        )
                      : undefined
                  }
                  onChange={(opt) => setNewSiteTimezone(opt?.value ?? null)}
                  options={ALLOY_ACCESS_TIMEZONES}
                  getOptionValue={(opt) => opt.value}
                  getOptionLabel={(opt) => opt.label}
                />
                <Button
                  disabled={
                    !newSiteName ||
                    !newSiteTimezone ||
                    linkSiteMutation.isLoading
                  }
                  onPress={handleCreateAndLinkSite}
                >
                  {linkSiteMutation.isLoading
                    ? "Creating..."
                    : "Create & Link Site"}
                </Button>
              </>
            )}
          </>
        ) : null}
      </DrawerContent>
      <DrawerActions>
        <Button onPress={pop} variation="plain">
          Close
        </Button>
      </DrawerActions>
    </>
  );
}

const styles = StyleSheet.create({
  drawerContent: { marginTop: -25 },
  toggleButton: {
    marginTop: 16,
    marginBottom: 16,
    alignSelf: "flex-start",
  },
  modeToggle: { marginTop: 12, marginBottom: 8 },
});
